import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth, useTheme } from "../App";
import { 
  Search, 
  Volume2, 
  Filter,
  Moon,
  Sun,
  Home,
  User,
  LogOut,
  BookOpen,
  Plus
} from "lucide-react";
import AudioButton from "../components/AudioButton";
import FavoriteButton from "../components/FavoriteButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dictionary() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const [words, setWords] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedLevel, setSelectedLevel] = useState("all");
  const [loading, setLoading] = useState(true);

  // Read URL params on mount
  useEffect(() => {
    const categoryFromUrl = searchParams.get('category');
    if (categoryFromUrl) {
      setSelectedCategory(categoryFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories
        const catResponse = await fetch(`${API}/categories`);
        if (catResponse.ok) {
          const catData = await catResponse.json();
          setCategories(catData);
        }

        // Fetch words
        await fetchWords();
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const fetchWords = async () => {
    setLoading(true);
    try {
      let url = `${API}/words?limit=100`;
      if (selectedCategory !== "all") {
        url += `&catégorie=${encodeURIComponent(selectedCategory)}`;
      }
      if (selectedLevel !== "all") {
        url += `&niveau=${encodeURIComponent(selectedLevel)}`;
      }
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setWords(data);
      }
    } catch (error) {
      console.error("Error fetching words:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchWords();
    }, 300);

    return () => clearTimeout(debounce);
  }, [search, selectedCategory, selectedLevel]);

  return (
    <div className="min-h-screen bg-background" data-testid="dictionary-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-heading font-bold text-xl">B</span>
              </div>
              <span className="font-heading font-bold text-xl hidden sm:block">Apprends Baoulé</span>
            </Link>

            <div className="flex items-center gap-3">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              
              {user ? (
                <>
                  <Link to="/dashboard" className="p-2 rounded-lg hover:bg-muted transition-colors">
                    <Home className="w-5 h-5" />
                  </Link>
                  <Link to="/profile" className="p-2 rounded-lg hover:bg-muted transition-colors">
                    <User className="w-5 h-5" />
                  </Link>
                </>
              ) : (
                <Link to="/login">
                  <Button variant="outline" size="sm">Connexion</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-20 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 animate-slide-up">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="font-heading text-3xl font-bold mb-2">Dictionnaire Baoulé</h1>
                <p className="text-muted-foreground">
                  Explorez {words.length} mots et expressions
                </p>
              </div>
              {user && (
                <Link to="/community">
                  <Button className="rounded-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Contribuer
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Filters */}
          <Card className="border-border/50 mb-8 animate-slide-up stagger-1">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Search */}
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher un mot..."
                    className="pl-10"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="search-input"
                  />
                </div>

                {/* Category Filter */}
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les catégories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Level Filter */}
                <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                  <SelectTrigger className="w-full sm:w-40" data-testid="level-filter">
                    <SelectValue placeholder="Niveau" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous niveaux</SelectItem>
                    <SelectItem value="débutant">Débutant</SelectItem>
                    <SelectItem value="intermédiaire">Intermédiaire</SelectItem>
                    <SelectItem value="avancé">Avancé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Words Grid */}
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-6 bg-muted rounded w-3/4 mb-3"></div>
                    <div className="h-4 bg-muted rounded w-1/2 mb-2"></div>
                    <div className="h-4 bg-muted rounded w-1/4"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : words.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading text-xl font-semibold mb-2">Aucun mot trouvé</h3>
              <p className="text-muted-foreground">
                Essayez de modifier vos filtres ou votre recherche
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-fade-in">
              {words.map((word) => (
                <Card 
                  key={word.word_id} 
                  className="word-card border-border/50 hover:border-primary/30"
                  data-testid={`word-card-${word.word_id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-heading text-xl font-bold">{word.baoulé}</h3>
                        {word.phonétique && (
                          <p className="font-mono text-sm text-muted-foreground">
                            /{word.phonétique}/
                          </p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <FavoriteButton 
                          wordId={word.word_id}
                          size="md"
                        />
                        <AudioButton 
                          text={word.baoulé}
                          phonetic={word.phonétique}
                          size="md"
                        />
                      </div>
                    </div>

                    <p className="text-lg mb-3">{word.français}</p>

                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded-full bg-muted text-xs">
                        {word.catégorie}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        word.niveau === "débutant" 
                          ? "bg-accent/10 text-accent"
                          : word.niveau === "intermédiaire"
                          ? "bg-primary/10 text-primary"
                          : "bg-secondary/10 text-secondary"
                      }`}>
                        {word.niveau}
                      </span>
                    </div>

                    {word.exemple_baoulé && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <p className="text-sm italic text-muted-foreground">
                          "{word.exemple_baoulé}"
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {word.exemple_français}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
